import React, { useMemo, useState } from 'react';
import { MS_TOPICS } from '../../messages/mockData';
import type { MsThread, MsTopicKey } from '../../messages/types';
import { useAgentHold } from '../../team-hold/teamHold';
import { Icon } from '../Icon/Icon';
import { MessagesConvRow } from './MessagesConvRow';

const TOPIC_KEYS = Object.keys(MS_TOPICS) as MsTopicKey[];

export type MessagesInboxTab = 'all' | 'unread' | 'manual' | 'awaiting' | 'archived';

export interface MessagesInboxProps {
  activeId: string;
  onSelect: (id: string) => void;
  conversations?: MsThread[];
  activeTab?: MessagesInboxTab;
  onTabChange?: (tab: MessagesInboxTab) => void;
  /** Counts for the client-side tabs, from the default (open) dataset. */
  counts?: { all: number; unread: number; manual: number };
  /** True when `conversations` is already server-filtered (Awaiting/Archived) — skip client filtering. */
  serverFiltered?: boolean;
  loading?: boolean;
}

/** Left inbox column — matches reference `MsInbox`. */
export function MessagesInbox({
  activeId,
  onSelect,
  conversations: conversationsProp = [],
  activeTab = 'all',
  onTabChange,
  counts,
  serverFiltered = false,
  loading = false,
}: MessagesInboxProps) {
  const allConversations = conversationsProp;
  const [q, setQ] = useState('');
  const [topicFilter, setTopicFilter] = useState<MsTopicKey | null>(null);
  const [filterOpen, setFilterOpen] = useState(false);

  const list = useMemo(() => {
    let r = [...allConversations];
    // Awaiting/Archived are filtered by the server; All/Unread/Manual filter client-side.
    if (!serverFiltered) {
      if (activeTab === 'unread') r = r.filter((c) => c.unread > 0);
      // "Manual" (formerly "Mine") — threads staff handle by hand: autopilot off, not opted out.
      if (activeTab === 'manual') r = r.filter((c) => !c.autoRespondOn && !c.optedOut);
    }
    if (topicFilter) {
      r = r.filter((c) => c.currentTopic === topicFilter || c.topics.includes(topicFilter));
    }
    if (q.trim()) {
      const s = q.toLowerCase();
      r = r.filter(
        (c) =>
          c.name.toLowerCase().includes(s) ||
          c.phone.includes(s) ||
          c.preview.toLowerCase().includes(s),
      );
    }
    return r.sort(
      (a, b) =>
        (b.currentTopic === 'emergency' ? 1 : 0) - (a.currentTopic === 'emergency' ? 1 : 0),
    );
  }, [allConversations, activeTab, q, serverFiltered, topicFilter]);

  const allN = counts?.all ?? allConversations.length;
  const unreadN = counts?.unread ?? allConversations.filter((c) => c.unread > 0).length;
  const manualN = counts?.manual ?? allConversations.filter((c) => !c.autoRespondOn && !c.optedOut).length;
  const smsHeld = useAgentHold('sms').held;
  const tabs: [MessagesInboxTab, string, number | null][] = [
    ['all', 'All', allN],
    ['unread', 'Unread', unreadN],
    ['manual', 'Manual', manualN],
    ['awaiting', 'Awaiting', activeTab === 'awaiting' ? allConversations.length : null],
    ['archived', 'Archived', activeTab === 'archived' ? allConversations.length : null],
  ];

  return (
    <div
      style={{
        width: 344,
        flex: 'none',
        display: 'flex',
        flexDirection: 'column',
        borderRight: '1px solid var(--cream-2)',
        background: 'var(--cream-0)',
        minHeight: 0,
      }}
    >
      {smsHeld ? (
        <div
          role="status"
          style={{
            display: 'flex', alignItems: 'center', gap: 8, padding: '8px 16px', flex: 'none',
            borderBottom: '1px solid var(--cream-2)', background: 'var(--gold-tint)',
            color: '#9A6B1E', fontSize: 12, fontWeight: 600,
          }}
        >
          <Icon kind="pause" size={13} stroke={2} />
          AI SMS is on hold — your team answers
        </div>
      ) : null}
      <div style={{ padding: '14px 16px 12px', borderBottom: '1px solid var(--cream-2)', flex: 'none' }}>
        <div
          style={{
            background: 'var(--cream-1)',
            border: '1px solid var(--cream-2)',
            borderRadius: 'var(--r-lg)',
            boxShadow: 'var(--sh-1)',
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '0 6px 0 13px',
              height: 40,
              borderBottom: '1px solid var(--cream-2)',
            }}
          >
            <Icon kind="search" size={14} style={{ color: 'var(--ink-4)' }} />
            <input
              value={q}
              onChange={(e) => {
                setQ(e.target.value);
              }}
              placeholder="Search conversations…"
              style={{
                flex: 1,
                border: 0,
                background: 'transparent',
                outline: 'none',
                fontFamily: 'var(--font-body)',
                fontSize: 13,
                color: 'var(--ink-1)',
              }}
            />
            <div style={{ position: 'relative', flex: 'none' }}>
              <button
                type="button"
                title="Filter by topic"
                style={{
                  width: 30,
                  height: 30,
                  borderRadius: 'var(--r-sm)',
                  border: 'none',
                  background: topicFilter || filterOpen ? 'var(--ai-tint)' : 'transparent',
                  color: topicFilter ? 'var(--ai-3)' : 'var(--ink-3)',
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flex: 'none',
                }}
                onClick={() => {
                  setFilterOpen((v) => !v);
                }}
              >
                <Icon kind="filter" size={15} />
              </button>
              {filterOpen ? (
                <>
                  <div
                    onClick={() => {
                      setFilterOpen(false);
                    }}
                    style={{ position: 'fixed', inset: 0, zIndex: 40 }}
                  />
                  <div
                    style={{
                      position: 'absolute',
                      top: 'calc(100% + 6px)',
                      right: 0,
                      zIndex: 50,
                      minWidth: 214,
                      maxHeight: 340,
                      overflowY: 'auto',
                      background: 'var(--cream-0)',
                      border: '1px solid var(--cream-2)',
                      borderRadius: 'var(--r-md)',
                      boxShadow: 'var(--sh-3)',
                      padding: 5,
                    }}
                  >
                    <div
                      style={{
                        padding: '4px 9px 6px',
                        fontFamily: 'var(--font-mono)',
                        fontSize: 9.5,
                        fontWeight: 700,
                        letterSpacing: '0.12em',
                        textTransform: 'uppercase',
                        color: 'var(--ink-4)',
                      }}
                    >
                      Filter by topic
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setTopicFilter(null);
                        setFilterOpen(false);
                      }}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        width: '100%',
                        padding: '7px 9px',
                        border: 'none',
                        background: 'transparent',
                        cursor: 'pointer',
                        borderRadius: 'var(--r-sm)',
                        textAlign: 'left',
                        fontSize: 12.5,
                        fontWeight: topicFilter === null ? 700 : 500,
                        color: 'var(--ink-1)',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = 'var(--cream-1)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'transparent';
                      }}
                    >
                      All topics
                      {topicFilter === null ? (
                        <Icon
                          kind="check"
                          size={13}
                          style={{ marginLeft: 'auto', color: 'var(--ai-3)' }}
                        />
                      ) : null}
                    </button>
                    {TOPIC_KEYS.map((k) => {
                      const cnt = allConversations.filter(
                        (c) => c.currentTopic === k || c.topics.includes(k),
                      ).length;
                      const meta = MS_TOPICS[k];
                      return (
                        <button
                          key={k}
                          type="button"
                          onClick={() => {
                            setTopicFilter(k);
                            setFilterOpen(false);
                          }}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                            width: '100%',
                            padding: '5px 9px',
                            border: 'none',
                            background: 'transparent',
                            cursor: 'pointer',
                            borderRadius: 'var(--r-sm)',
                            textAlign: 'left',
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = 'var(--cream-1)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = 'transparent';
                          }}
                        >
                          <span
                            style={{
                              width: 8,
                              height: 8,
                              borderRadius: 999,
                              background: meta.color,
                              flex: 'none',
                            }}
                          />
                          <span
                            style={{
                              flex: 1,
                              fontSize: 12.5,
                              fontWeight: topicFilter === k ? 700 : 500,
                              color: 'var(--ink-1)',
                            }}
                          >
                            {meta.label}
                          </span>
                          <span
                            style={{
                              fontFamily: 'var(--font-mono)',
                              fontSize: 10.5,
                              fontWeight: 700,
                              color: 'var(--ink-3)',
                            }}
                          >
                            {cnt}
                          </span>
                          {topicFilter === k ? (
                            <Icon kind="check" size={13} style={{ color: 'var(--ai-3)' }} />
                          ) : null}
                        </button>
                      );
                    })}
                  </div>
                </>
              ) : null}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 4, padding: '7px 8px' }}>
            {tabs.map(([k, label, n]) => (
              <button
                key={k}
                type="button"
                className={`ms-state${activeTab === k ? ' active' : ''}`}
                style={{ flex: 1, justifyContent: 'center' }}
                onClick={() => {
                  onTabChange?.(k);
                }}
              >
                {label}
                {typeof n === 'number' && n > 0 ? (
                  <span
                    style={{
                      fontFamily: 'var(--font-mono)',
                      fontSize: 10,
                      fontWeight: 700,
                      color: k === 'unread' ? 'var(--terracotta)' : 'var(--ink-3)',
                    }}
                  >
                    {n}
                  </span>
                ) : null}
              </button>
            ))}
          </div>
        </div>
        {topicFilter ? (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              marginTop: 10,
            }}
          >
            <span style={{ fontSize: 11, color: 'var(--ink-4)' }}>Filtered:</span>
            <span
              style={{
                width: 8,
                height: 8,
                borderRadius: 999,
                background: MS_TOPICS[topicFilter].color,
                flex: 'none',
              }}
            />
            <span style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--ink-1)' }}>
              {MS_TOPICS[topicFilter].label}
            </span>
            <button
              type="button"
              onClick={() => {
                setTopicFilter(null);
              }}
              className="ms-chip"
              style={{ marginLeft: 'auto', padding: '3px 8px', fontSize: 11 }}
            >
              <Icon kind="x" size={11} /> Clear
            </button>
          </div>
        ) : null}
      </div>

      <div className="cal-scroll ms-conv-list" style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
        {list.map((c) => (
          <MessagesConvRow
            key={c.id}
            thread={c}
            active={c.id === activeId}
            onClick={() => {
              onSelect(c.id);
            }}
          />
        ))}
        {list.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--ink-4)', fontSize: 13 }}>
            {loading
              ? 'Loading…'
              : activeTab === 'archived'
                ? 'No archived conversations.'
                : activeTab === 'awaiting'
                  ? 'No one is awaiting a reply.'
                  : 'No conversations match.'}
          </div>
        ) : null}
      </div>
    </div>
  );
}
